import {
  extractErrorMessage,
  useApiQualitySessionsV1StartCopyEvidenceToTestCompanyJobCreate,
  useCurrentUser,
} from 'api';
import { useNotification } from 'features';
import { Trans, useTranslation } from 'i18n';
import { ChangeEvent, useMemo, useState } from 'react';
import { Box, Button, Checkbox, FilePicker, FormControlLabel, Page, Section, Typography } from 'ui';

export const CopySessions = () => {
  const { t } = useTranslation('orders');
  const { open } = useNotification();
  const { data: user, isLoading } = useCurrentUser();
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [overrideExistingSessions, setOverrideExistingSessions] = useState(false);
  const [lastExecutionId, setLastExecutionId] = useState<string | null>(null);

  const isAdmin = useMemo(() => Boolean(user?.is_superuser), [user]);

  const { mutate, isPending } = useApiQualitySessionsV1StartCopyEvidenceToTestCompanyJobCreate({
    mutation: {
      onError(error) {
        const description = extractErrorMessage(error);
        open({ description, message: t('copySessions.notifications.startJobFailed'), type: 'error' });
      },
      onSuccess(payload) {
        if (!payload.success) {
          open({ message: payload.error ?? t('copySessions.notifications.jobExecutionFailed'), type: 'error' });
          return;
        }

        setLastExecutionId(payload.execution_id ?? null);
        open({ message: t('copySessions.notifications.jobStarted'), type: 'success' });
      },
    },
  });

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    setCsvFile(file);
  };

  const handleSubmit = () => {
    if (!csvFile) {
      open({ message: t('copySessions.notifications.selectCsvFile'), type: 'error' });
      return;
    }

    setLastExecutionId(null);
    mutate({
      data: {
        // @ts-expect-error API expects a string but we should send the File object
        csv_file: csvFile,
        override_existing_sessions: overrideExistingSessions ? true : undefined,
      },
    });
  };

  return (
    <Page>
      <Page.Title title={t('copySessions.pageTitle')} />
      <Page.Divider />
      {isLoading ? (
        <Typography>{t('copySessions.states.loading')}</Typography>
      ) : !isAdmin ? (
        <Typography>{t('copySessions.states.adminOnly')}</Typography>
      ) : (
        <Section>
          <Typography
            sx={{
              mb: 2,
            }}
          >
            <Trans
              namespace="orders"
              i18nKey="copySessions.description.uploadCsvInstructions"
              components={{
                newName: <strong />,
                sourceTradeCompanySessionId: <strong />,
              }}
              values={{
                newName: 'new_name',
                sourceTradeCompanySessionId: 'source_trade_company_session_id',
              }}
            />
          </Typography>
          <Box
            sx={{
              mb: 2,
            }}
          >
            <FilePicker
              accept=".csv"
              label={t('copySessions.form.labels.csvFile')}
              onChange={handleFileChange}
              showSelectedFileName
            />
          </Box>
          <Box
            sx={{
              mb: 2,
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={overrideExistingSessions}
                  onChange={(event) => {
                    setOverrideExistingSessions(event.target.checked);
                  }}
                />
              }
              label={t('copySessions.form.labels.overrideExistingSessions')}
            />
          </Box>
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              gap: 2,
            }}
          >
            <Button onClick={handleSubmit} loading={isPending}>
              {t('copySessions.form.actions.startJob')}
            </Button>
            {lastExecutionId ? (
              <Typography>
                {t('copySessions.form.values.executionId', { values: { executionId: lastExecutionId } })}
              </Typography>
            ) : null}
          </Box>
        </Section>
      )}
    </Page>
  );
};
